import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { format, isSameDay, eachDayOfInterval } from "date-fns";
import { Send, Loader2, CheckCircle2, PenLine, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls =
  "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition";
const qtyCls =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition disabled:opacity-50";

const SERVICE_TYPES = ["Private boarding", "Home visits", "Medication assistance"];
const BEHAVIOR = ["Biting or aggression", "Resource guarding", "Fighting with animals", "Fear of people or animals", "Separation anxiety", "Escaping or climbing fences", "Destructive behavior", "Excessive barking", "Other"];
const OWNER_AGREEMENTS = [
  "I am the pet's owner or have authority to arrange the pet's care.",
  "The information I provided is complete and accurate.",
  "I have disclosed all known medical conditions, medications, aggressive behavior, bite history, escape behavior, and special needs.",
  "I authorize Cozy Pet Care to provide the services and follow the instructions listed in this agreement.",
  "I authorize reasonable adjustments when necessary for my pet's health or safety.",
  "I accept the ordinary risks associated with pet boarding, home visits, walks, transportation, interaction with people, and interaction with other animals.",
  "I am responsible for agreed charges, veterinary expenses, additional supplies, extended care, and damage caused by my pet.",
  "I have received and agree to Cozy Pet Care's Terms and Conditions and Cancellation Policy.",
  "I will remain reachable or provide an emergency contact who can make decisions during the service period.",
  "I will notify Cozy Pet Care promptly of changes to my pet's health, behavior, medications, or care instructions.",
];
const PHOTO_PRIVATE = [
  "Cozy Pet Care may photograph my pet and use the photographs privately to provide me daily updates.",
  "I do not wish photographs of my pet to be sent to me privately.",
];
const PHOTO_PUBLIC = [
  "Cozy Pet Care may also use photographs of my pet on its website, social media, or advertising.",
  "I do not wish photographs of my pet to be used publicly.",
];
const EM_AUTH = [
  "Attempt to contact me and my emergency contact;",
  "Contact my regular veterinarian or an emergency veterinary facility;",
  "Transport or arrange transportation for my pet;",
  "Share relevant health and care information with veterinary personnel; and",
  "Authorize examination, diagnostic testing, stabilization, pain relief, medication, and other treatment reasonably considered necessary when I cannot be reached promptly.",
];
const SPENDING = [
  "I authorize emergency veterinary care up to a specified amount without additional approval.",
  "I authorize all treatment reasonably necessary to stabilize or protect my pet until I can be reached.",
  "I authorize examination and stabilization only. Contact me before additional treatment unless delaying treatment would cause unnecessary suffering or create an immediate threat to my pet's life.",
];
const RESUS = [
  "Authorize resuscitation: I authorize CPR and other reasonable lifesaving measures.",
  "Do not authorize resuscitation: I do not authorize CPR if my pet experiences cardiac or respiratory arrest.",
];

const TIME_OPTIONS = (() => {
  const arr = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const period = h < 12 ? "AM" : "PM";
      let hh = h % 12;
      if (hh === 0) hh = 12;
      arr.push(`${hh}:${m === 0 ? "00" : "30"} ${period}`);
    }
  }
  return arr;
})();

const formatPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  const len = d.length;
  if (len === 0) return "";
  if (len < 4) return `(${d}`;
  if (len < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const formatCurrency = (v) => {
  const num = parseFloat((v || "").replace(/[^0-9.]/g, ""));
  if (isNaN(num)) return "";
  return "$" + num.toFixed(2);
};

// ---- module-level primitives (defined once, so inputs never remount / lose focus) ----
const Field = ({ label, children }) => (
  <div className="mb-4">
    {label && <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>}
    {children}
  </div>
);
const SecH = ({ children }) => <h3 className="font-heading text-xl font-semibold text-foreground mt-9 mb-4 first:mt-0 pb-2 border-b border-border">{children}</h3>;
const SubH = ({ children }) => <h4 className="font-heading text-lg font-semibold text-foreground mt-6 mb-2">{children}</h4>;

const CheckBox = ({ checked, onClick, label, testid, single }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="flex items-start gap-3 text-left w-full group">
    <span className={`mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center border-2 transition ${single ? "rounded-full" : "rounded-[5px]"} ${checked ? "bg-primary border-primary" : "border-primary/50 group-hover:border-primary"}`}>
      {checked && <CheckCircle2 className="w-3.5 h-3.5 text-primary-foreground" />}
    </span>
    <span className="text-base text-muted-foreground leading-relaxed">{label}</span>
  </button>
);

const Sig = ({ value, onChange, testid }) => (
  <div className="flex items-center rounded-xl border border-border bg-background focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary transition">
    <span className="pl-3 pr-2 text-primary font-semibold select-none inline-flex items-center gap-1"><PenLine className="w-4 h-4" />/s/</span>
    <input data-testid={testid} type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Type your full name" className="flex-1 bg-transparent px-1 py-2.5 pr-4 text-base text-foreground italic focus:outline-none" />
  </div>
);

const DateField = ({ value, onChange, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid={testid} className={`${inputCls} flex items-center justify-between text-left`}>
        <span className={value ? "text-foreground" : "text-muted-foreground/60"}>{value ? format(value, "MMM d, yyyy") : "Select date"}</span>
        <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
    </PopoverContent>
  </Popover>
);

const TimeSelect = ({ value, onChange, testid }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-11 rounded-xl text-base">
      <SelectValue placeholder="Select time" />
    </SelectTrigger>
    <SelectContent className="max-h-64">
      {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
    </SelectContent>
  </Select>
);

const PartBanner = ({ position, total, title }) => (
  <div className="mb-6 mt-4 first:mt-0 rounded-2xl bg-primary/10 border border-primary/25 px-5 py-4">
    <span className="text-xs font-semibold tracking-widest text-primary uppercase">Part {position} of {total}</span>
    <h2 className="font-heading text-2xl font-semibold text-foreground mt-1">{title}</h2>
  </div>
);

// Veterinarian field block (shared state → same values show in Agreement + Emergency).
const VetFields = ({ v, onChange, tid }) => (
  <>
    <Field label="Veterinarian Business Name"><input data-testid={`${tid}-business`} className={inputCls} value={v.vetBusiness || ""} onChange={(e) => onChange("vetBusiness", e.target.value)} /></Field>
    <Field label="Veterinarian Name"><input data-testid={`${tid}-name`} className={inputCls} value={v.vetName || ""} onChange={(e) => onChange("vetName", e.target.value)} /></Field>
    <Field label="Veterinarian Phone"><input data-testid={`${tid}-phone`} inputMode="tel" className={inputCls} value={v.vetPhone || ""} onChange={(e) => onChange("vetPhone", formatPhone(e.target.value))} placeholder="(555) 555-5555" /></Field>
    <Field label="Veterinarian Address"><input data-testid={`${tid}-address`} className={inputCls} value={v.vetAddress || ""} onChange={(e) => onChange("vetAddress", e.target.value)} /></Field>
  </>
);

const emptyPet = () => ({ name: "", breed: "", age: "", sex: "", fixed: "", insurance: "", policy: "", vetBusiness: "", vetName: "", vetPhone: "", vetAddress: "", feedings: [] });
const emptyFeed = () => ({ time: "", dry: false, dryQty: "", wet: false, wetQty: "", mixed: false, mixedQty: "" });
const today = () => new Date();
// Groups selected days into "MMM d, yyyy" singles and "MMM d – MMM d, yyyy" contiguous ranges.
const formatDateGroups = (dates) => {
  if (!dates || !dates.length) return "";
  const s = [...dates].sort((a, b) => a - b);
  const groups = [];
  let start = s[0], prev = s[0];
  for (let i = 1; i < s.length; i++) {
    const diff = Math.round((s[i] - prev) / 86400000);
    if (diff === 1) { prev = s[i]; continue; }
    groups.push([start, prev]); start = s[i]; prev = s[i];
  }
  groups.push([start, prev]);
  const parts = groups.map(([a, b]) => (isSameDay(a, b) ? format(a, "MMM d") : `${format(a, "MMM d")} – ${format(b, "MMM d")}`));
  return `${parts.join(", ")}, ${format(s[s.length - 1], "yyyy")}`;
};

export const CombinedPolicyForm = ({ scrollTo }) => {
  const [f, setState] = useState({
    ownerName: "", address: "", ownerPhone: "", email: "", emContactName: "", emContactPhone: "",
    numPets: 1, pets: [emptyPet()],
    differentVets: false, vetBusiness: "", vetName: "", vetPhone: "", vetAddress: "",
    serviceTypes: [], serviceDates: [], dropoff: "", pickup: "", price: "",
    feedingsPerDay: "", feedingNotes: "",
    medications: "", behavior: [], behaviorExplain: "", health: "",
    photoPrivate: "", photoPublic: "",
    ownerPrinted: "", ownerSig: "", signDate: today(),
    emSecondary: "", emEmerContact: "", emEmerPhone: "",
    emInsurance: "", emPolicy: "",
    spending: "", resus: "",
    emPrinted: "", emSig: "", emDate: today(),
  });
  const [sendName, setSendName] = useState("");
  const [sendNameTouched, setSendNameTouched] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendEmailTouched, setSendEmailTouched] = useState(false);
  const [ownerPrintedTouched, setOwnerPrintedTouched] = useState(false);
  const [emPrintedTouched, setEmPrintedTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const lastDayRef = useRef(null);

  const set = (k, v) => setState((p) => ({ ...p, [k]: v }));
  const setPhone = (k, v) => set(k, formatPhone(v));
  const setPet = (i, k, v) => setState((p) => ({ ...p, pets: p.pets.map((pt, idx) => (idx === i ? { ...pt, [k]: v } : pt)) }));
  const setPetFeed = (pi, fi, k, v) => setState((p) => ({ ...p, pets: p.pets.map((pt, idx) => (idx === pi ? { ...pt, feedings: pt.feedings.map((ft, j) => (j === fi ? { ...ft, [k]: v } : ft)) } : pt)) }));
  const setFeedingsPerDay = (val) => setState((p) => {
    const clean = val.replace(/\D/g, "");
    const n = Math.min(10, parseInt(clean, 10) || 0);
    const pets = p.pets.map((pt) => {
      const feedings = [...(pt.feedings || [])];
      while (feedings.length < n) feedings.push(emptyFeed());
      feedings.length = n;
      return { ...pt, feedings };
    });
    return { ...p, feedingsPerDay: clean, pets };
  });
  const setNumPets = (n) => setState((p) => {
    const num = Math.max(1, Math.min(6, parseInt(n, 10) || 1));
    const fpd = parseInt(p.feedingsPerDay, 10) || 0;
    const pets = [...p.pets];
    while (pets.length < num) pets.push({ ...emptyPet(), feedings: Array.from({ length: fpd }, () => emptyFeed()) });
    pets.length = num;
    return { ...p, numPets: num, pets };
  });
  const toggleArr = (k, item) => setState((p) => {
    const cur = p[k] || [];
    return { ...p, [k]: cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item] };
  });

  const handleServiceDayClick = (day, modifiers, e) => {
    setState((p) => {
      const cur = p.serviceDates || [];
      if (e.shiftKey && lastDayRef.current) {
        const a = lastDayRef.current;
        const start = a < day ? a : day;
        const end = a < day ? day : a;
        const merged = [...cur];
        eachDayOfInterval({ start, end }).forEach((d) => { if (!merged.some((m) => isSameDay(m, d))) merged.push(d); });
        lastDayRef.current = day;
        return { ...p, serviceDates: merged };
      }
      lastDayRef.current = day;
      const exists = cur.some((d) => isSameDay(d, day));
      return { ...p, serviceDates: exists ? cur.filter((d) => !isSameDay(d, day)) : [...cur, day] };
    });
  };
  const serviceDatesLabel = () => formatDateGroups(f.serviceDates) || "Select dates";

  useEffect(() => {
    if (!scrollTo || done) return;
    const t = setTimeout(() => {
      const el = document.getElementById(scrollTo);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: "smooth" });
    }, 350);
    return () => clearTimeout(t);
  }, [scrollTo, done]);

  const D = (d) => (d ? format(d, "MMM d, yyyy") : "");
  const sig = (v) => (v ? `/s/ ${v}` : "");
  const perPetVet = f.numPets > 1 && f.differentVets;
  const effName = sendNameTouched ? sendName : f.ownerName;
  const effEmail = sendEmailTouched ? sendEmail : f.email;
  const effOwnerPrinted = ownerPrintedTouched ? f.ownerPrinted : f.ownerName;
  const effEmPrinted = emPrintedTouched ? f.emPrinted : f.ownerName;

  const buildEntries = () => {
    const e = [];
    const S = (label) => e.push({ kind: "section", label });
    const F = (label, value) => e.push({ kind: "field", label, value: value || "" });
    const vetOf = (v, tag) => { F(`${tag}Veterinarian Business Name`, v.vetBusiness); F(`${tag}Veterinarian Name`, v.vetName); F(`${tag}Veterinarian Phone`, v.vetPhone); F(`${tag}Veterinarian Address`, v.vetAddress); };

    S("PART 1 — PET CARE AGREEMENT");
    S("Owner Information");
    F("Owner's Name", f.ownerName); F("Address", f.address); F("Phone", f.ownerPhone); F("Email", f.email);
    F("Secondary Emergency Contact Name (Owner is Primary Contact)", f.emContactName); F("Secondary Emergency Contact Phone", f.emContactPhone);
    S("Pet Information");
    F("Number of Pets", String(f.numPets));
    f.pets.forEach((p, i) => {
      S(`Pet ${i + 1}`);
      F("Pet's Name", p.name); F("Type / Breed", p.breed); F("Age", p.age); F("Sex", p.sex); F("Spayed or Neutered", p.fixed);
      F("Pet Insurance Company", p.insurance); F("Policy Number", p.policy);
    });
    S("Scheduled Care");
    F("Service Type", f.serviceTypes.join(", "));
    F("Service Dates", formatDateGroups(f.serviceDates));
    F("Drop-Off / Start Time", f.dropoff); F("Pickup / End Time", f.pickup); F("Agreed Price (per our phone conversation)", f.price);
    S("Feeding Instructions");
    F("Number of feedings per day", f.feedingsPerDay);
    f.pets.forEach((pt, pi) => {
      if (f.numPets > 1) S(`Pet ${pi + 1} — ${pt.name || "Pet"} feeding schedule`);
      (pt.feedings || []).forEach((fd, fi) => {
        const types = [];
        if (fd.dry) types.push(`Dry${fd.dryQty ? ` (${fd.dryQty})` : ""}`);
        if (fd.wet) types.push(`Wet${fd.wetQty ? ` (${fd.wetQty})` : ""}`);
        if (fd.mixed) types.push(`Mixed dry & wet${fd.mixedQty ? ` (${fd.mixedQty})` : ""}`);
        if (fd.time || types.length) F(`Feeding ${fi + 1}`, `${fd.time || "—"}${types.length ? ": " + types.join(", ") : ""}`);
      });
    });
    if (f.feedingNotes) F("Other feeding notes", f.feedingNotes);
    S("Medication Instructions"); F("Medications", f.medications);
    S("Behavior and Safety"); F("History", f.behavior.join(", ")); F("Explanation", f.behaviorExplain);
    S("Photo Permission"); F("Private photos", f.photoPrivate); F("Public photos", f.photoPublic);
    S("Signature"); F("Owner's Printed Name", effOwnerPrinted); F("Owner's Digital Signature", sig(f.ownerSig)); F("Date", D(f.signDate));

    S("PART 2 — EMERGENCY CARE AUTHORIZATION");
    S("Health Information"); F("Notes", f.health);
    S("Veterinary Information");
    if (perPetVet) f.pets.forEach((p, i) => { S(`Pet ${i + 1} — ${p.name || "Veterinarian"}`); vetOf(p, ""); });
    else vetOf(f, "");
    S("Spending Authorization"); F("Selection", f.spending);
    S("Resuscitation Instructions"); F("Selection", f.resus);
    S("Signature"); F("Owner's Printed Name", effEmPrinted); F("Owner's Digital Signature", sig(f.emSig)); F("Date", D(f.emDate));
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!effEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(effEmail)) {
      toast.error("Please enter a valid email so we can send you a copy.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/policy-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_type: "Pet Care Agreement & Emergency Care Authorization", submitter_email: effEmail, submitter_name: effName, entries: buildEntries() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send. Please try again.");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Sent! Both forms are on their way to Cozy Pet Care.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please call or text us.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="policy-form-success" className="rounded-3xl bg-card p-8 sm:p-12 soft-shadow text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-primary/15 items-center justify-center mb-5">
          <CheckCircle2 className="w-8 h-8 text-primary" />
        </span>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">Thank you — both forms were sent.</h2>
        <p className="text-base text-muted-foreground leading-relaxed max-w-md mx-auto">
          We've emailed a copy to <span className="font-medium text-foreground">{effEmail}</span> and delivered your Pet Care Agreement and Emergency Care Authorization to Cozy Pet Care. We'll be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="policy-form" className="rounded-3xl bg-card p-7 sm:p-10 soft-shadow">
      <div className="rounded-2xl bg-accent/50 px-5 py-4 mb-8 text-sm text-muted-foreground">
        Please complete <span className="font-medium text-foreground">both parts</span> below. When you press <span className="font-medium text-foreground">Send</span>, both parts are delivered together in one email — a copy for us and a copy for you.
      </div>

      {/* ============ PART 1 — PET CARE AGREEMENT ============ */}
      <section id="agreement" data-testid="form-part-agreement" className="scroll-mt-28">
        <PartBanner position={1} total={2} title="Pet Care Agreement" />

        <SecH>Owner Information</SecH>
        <Field label="Owner's Name"><input data-testid="agr-owner-name" className={inputCls} value={f.ownerName} onChange={(e) => set("ownerName", e.target.value)} /></Field>
        <Field label="Address"><input data-testid="agr-owner-address" className={inputCls} value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
        <Field label="Phone"><input data-testid="agr-owner-phone" inputMode="tel" className={inputCls} value={f.ownerPhone} onChange={(e) => setPhone("ownerPhone", e.target.value)} placeholder="(555) 555-5555" /></Field>
        <Field label="Email"><input data-testid="agr-owner-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Secondary Emergency Contact Name (Owner is Primary Contact)"><input data-testid="agr-emc-name" autoComplete="off" className={inputCls} value={f.emContactName} onChange={(e) => set("emContactName", e.target.value)} /></Field>
        <Field label="Secondary Emergency Contact Phone"><input data-testid="agr-emc-phone" autoComplete="off" inputMode="tel" className={inputCls} value={f.emContactPhone} onChange={(e) => setPhone("emContactPhone", e.target.value)} placeholder="(555) 555-5555" /></Field>

        <SecH>Pet Information</SecH>
        <Field label="Number of Pets">
          <Select value={String(f.numPets)} onValueChange={setNumPets}>
            <SelectTrigger data-testid="agr-num-pets" className="h-11 rounded-xl text-base w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {f.pets.map((p, i) => (
          <div key={i} data-testid={`agr-pet-${i}`} className="rounded-2xl border border-border p-5 mb-4">
            <SubH>{f.numPets > 1 ? `Pet ${i + 1}` : "Pet Details"}</SubH>
            <Field label="Pet's Name"><input data-testid={`agr-pet-name-${i}`} className={inputCls} value={p.name} onChange={(e) => setPet(i, "name", e.target.value)} /></Field>
            <Field label="Type / Breed"><input data-testid={`agr-pet-breed-${i}`} className={inputCls} value={p.breed} onChange={(e) => setPet(i, "breed", e.target.value)} /></Field>
            <Field label="Age"><input data-testid={`agr-pet-age-${i}`} className={inputCls} value={p.age} onChange={(e) => setPet(i, "age", e.target.value)} /></Field>
            <Field label="Sex">
              <div className="flex gap-8">
                <CheckBox testid={`agr-pet-sex-male-${i}`} single checked={p.sex === "Male"} onClick={() => setPet(i, "sex", p.sex === "Male" ? "" : "Male")} label="Male" />
                <CheckBox testid={`agr-pet-sex-female-${i}`} single checked={p.sex === "Female"} onClick={() => setPet(i, "sex", p.sex === "Female" ? "" : "Female")} label="Female" />
              </div>
            </Field>
            <Field label="Spayed or Neutered">
              <div className="flex gap-8">
                <CheckBox testid={`agr-pet-fixed-yes-${i}`} single checked={p.fixed === "Yes"} onClick={() => setPet(i, "fixed", p.fixed === "Yes" ? "" : "Yes")} label="Yes" />
                <CheckBox testid={`agr-pet-fixed-no-${i}`} single checked={p.fixed === "No"} onClick={() => setPet(i, "fixed", p.fixed === "No" ? "" : "No")} label="No" />
              </div>
            </Field>
            <Field label="Pet Insurance Company"><input data-testid={`agr-pet-insurance-${i}`} className={inputCls} value={p.insurance || ""} onChange={(e) => setPet(i, "insurance", e.target.value)} /></Field>
            <Field label="Policy Number"><input data-testid={`agr-pet-policy-${i}`} className={inputCls} value={p.policy || ""} onChange={(e) => setPet(i, "policy", e.target.value)} /></Field>
          </div>
        ))}

        <SecH>Scheduled Care</SecH>
        <Field label="Service Type">
          <div className="space-y-2.5">
            {SERVICE_TYPES.map((s, k) => (
              <CheckBox key={s} testid={`agr-service-${k}`} checked={f.serviceTypes.includes(s)} onClick={() => toggleArr("serviceTypes", s)} label={s} />
            ))}
          </div>
        </Field>
        <Field label="Service Dates">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="agr-service-dates" className={`${inputCls} flex items-center justify-between text-left`}>
                <span className={(f.serviceDates || []).length ? "text-foreground" : "text-muted-foreground/60"}>{serviceDatesLabel()}</span>
                <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="default" onDayClick={handleServiceDayClick} modifiers={{ selected: f.serviceDates || [] }} modifiersClassNames={{ selected: "bg-primary text-primary-foreground rounded-md" }} />
            </PopoverContent>
          </Popover>
          <p className="mt-2 text-xs text-muted-foreground">Select each day individually, or hold the <span className="font-medium">Shift</span> key to select all days between a start and end date.</p>
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Drop-Off / Start Time"><TimeSelect testid="agr-dropoff-time" value={f.dropoff} onChange={(v) => set("dropoff", v)} /></Field>
          <Field label="Pickup / End Time"><TimeSelect testid="agr-pickup-time" value={f.pickup} onChange={(v) => set("pickup", v)} /></Field>
        </div>
        <Field label="Agreed Price (per our phone conversation)">
          <input data-testid="agr-price" inputMode="decimal" className={inputCls} value={f.price} onChange={(e) => set("price", e.target.value)} onBlur={() => set("price", formatCurrency(f.price))} placeholder="$0.00" />
        </Field>

        <SecH>Feeding Instructions</SecH>
        <Field label="Number of feedings per day">
          <input data-testid="agr-feedings-per-day" inputMode="numeric" className={`${inputCls} w-32`} value={f.feedingsPerDay} onChange={(e) => setFeedingsPerDay(e.target.value)} />
        </Field>
        {!f.feedingsPerDay || parseInt(f.feedingsPerDay, 10) < 1 ? (
          <p className="text-sm text-muted-foreground mb-4">Enter the number of feedings per day to add feeding times for each pet.</p>
        ) : (
          f.pets.map((pt, pi) => (
            <div key={pi} className="mb-6">
              <SubH>{f.numPets > 1 ? `Pet ${pi + 1} — ${pt.name || "Pet"} feeding schedule` : "Feeding schedule"}</SubH>
              {(pt.feedings || []).map((fd, fi) => (
                <div key={fi} data-testid={`agr-feeding-${pi}-${fi}`} className="rounded-2xl border border-border p-5 mb-4">
                  <Field label={`Feeding ${fi + 1} — time of day`}><TimeSelect testid={`agr-feeding-time-${pi}-${fi}`} value={fd.time} onChange={(v) => setPetFeed(pi, fi, "time", v)} /></Field>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[
                      { key: "dry", qty: "dryQty", label: "Dry food" },
                      { key: "wet", qty: "wetQty", label: "Wet food" },
                      { key: "mixed", qty: "mixedQty", label: "Mixed dry & wet" },
                    ].map((opt) => (
                      <div key={opt.key} className="rounded-xl bg-accent/40 p-3">
                        <CheckBox testid={`agr-feeding-${pi}-${fi}-${opt.key}`} checked={fd[opt.key]} onClick={() => setPetFeed(pi, fi, opt.key, !fd[opt.key])} label={opt.label} />
                        <input data-testid={`agr-feeding-${pi}-${fi}-${opt.key}-qty`} className={`${qtyCls} mt-2`} placeholder="Quantity" value={fd[opt.qty]} onChange={(e) => setPetFeed(pi, fi, opt.qty, e.target.value)} disabled={!fd[opt.key]} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <Field label="Other feeding notes (optional)"><textarea data-testid="agr-feeding-notes" rows={3} className={`${inputCls} resize-y`} value={f.feedingNotes} onChange={(e) => set("feedingNotes", e.target.value)} /></Field>

        <SecH>Medication Instructions</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">List medication name, amount, method, and schedule:</p>
        <Field><textarea data-testid="agr-medications" rows={3} className={`${inputCls} resize-y`} value={f.medications} onChange={(e) => set("medications", e.target.value)} /></Field>

        <SecH>Behavior and Safety</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">Please identify any history of:</p>
        <div className="space-y-2.5 mb-4">
          {BEHAVIOR.map((b, k) => <CheckBox key={b} testid={`agr-behavior-${k}`} checked={f.behavior.includes(b)} onClick={() => toggleArr("behavior", b)} label={b} />)}
        </div>
        <Field label="Please explain"><textarea data-testid="agr-behavior-explain" rows={2} className={`${inputCls} resize-y`} value={f.behaviorExplain} onChange={(e) => set("behaviorExplain", e.target.value)} /></Field>

        <SecH>Owner Agreements</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">I certify that:</p>
        <ol className="list-decimal pl-5 space-y-2 mb-4 text-base text-muted-foreground marker:text-primary">
          {OWNER_AGREEMENTS.map((t) => <li key={t} className="leading-relaxed pl-1">{t}</li>)}
        </ol>

        <SecH>Photo Permission</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">Please select one:</p>
        <div className="space-y-2.5 mb-5">
          {PHOTO_PRIVATE.map((t, k) => <CheckBox key={t} single testid={`agr-photo-private-${k}`} checked={f.photoPrivate === t} onClick={() => set("photoPrivate", f.photoPrivate === t ? "" : t)} label={t} />)}
        </div>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">Please select one:</p>
        <div className="space-y-2.5 mb-5">
          {PHOTO_PUBLIC.map((t, k) => <CheckBox key={t} single testid={`agr-photo-public-${k}`} checked={f.photoPublic === t} onClick={() => set("photoPublic", f.photoPublic === t ? "" : t)} label={t} />)}
        </div>

        <SecH>Signature</SecH>
        <Field label="Owner's Printed Name"><input data-testid="agr-printed-name" className={inputCls} value={effOwnerPrinted} onChange={(e) => { setOwnerPrintedTouched(true); set("ownerPrinted", e.target.value); }} /></Field>
        <Field label="Owner's Digital Signature"><Sig testid="agr-signature" value={f.ownerSig} onChange={(v) => set("ownerSig", v)} /></Field>
        <Field label="Date"><DateField testid="agr-sign-date" value={f.signDate} onChange={(v) => set("signDate", v)} /></Field>
      </section>

      {/* ============ PART 2 — EMERGENCY CARE AUTHORIZATION ============ */}
      <section id="emergency" data-testid="form-part-emergency" className="scroll-mt-28 mt-4">
        <PartBanner position={2} total={2} title="Emergency Care Authorization" />
        <p className="text-base text-muted-foreground leading-relaxed mb-3">This authorization applies when my pet is receiving services from Cozy Pet Care.</p>

        <SecH>Health Information</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">List allergies, illnesses, injuries, disabilities, dietary restrictions, or special needs:</p>
        <Field><textarea data-testid="em-health" rows={3} className={`${inputCls} resize-y`} value={f.health} onChange={(e) => set("health", e.target.value)} /></Field>

        <SecH>Veterinary Information</SecH>
        {f.numPets > 1 && (
          <div className="mb-4"><CheckBox testid="em-diff-vet" checked={f.differentVets} onClick={() => set("differentVets", !f.differentVets)} label="Pets have different veterinarians" /></div>
        )}
        {perPetVet
          ? f.pets.map((p, i) => (
              <div key={i} className="rounded-2xl border border-border p-5 mb-4">
                <SubH>{`Pet ${i + 1} — ${p.name || "Veterinarian"}`}</SubH>
                <VetFields v={p} onChange={(k, val) => setPet(i, k, val)} tid={`em-vet-${i}`} />
              </div>
            ))
          : <VetFields v={f} onChange={set} tid="em-vet" />}

        <SecH>Emergency Authorization</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">If my pet becomes ill, is injured, escapes, or experiences another emergency, I authorize Cozy Pet Care to:</p>
        <ol className="list-decimal pl-5 space-y-2 mb-4 text-base text-muted-foreground marker:text-primary">
          {EM_AUTH.map((t) => <li key={t} className="leading-relaxed pl-1">{t}</li>)}
        </ol>

        <SecH>Spending Authorization</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">Please select one:</p>
        <div className="space-y-2.5 mb-5">
          {SPENDING.map((t, k) => <CheckBox key={t} single testid={`em-spending-${k}`} checked={f.spending === t} onClick={() => set("spending", f.spending === t ? "" : t)} label={t} />)}
        </div>

        <SecH>Resuscitation Instructions</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">Please select one:</p>
        <div className="space-y-2.5 mb-5">
          {RESUS.map((t, k) => <CheckBox key={t} single testid={`em-resus-${k}`} checked={f.resus === t} onClick={() => set("resus", f.resus === t ? "" : t)} label={t} />)}
        </div>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">I accept full financial responsibility for veterinary treatment, transportation, medication, supplies, and other emergency expenses involving my pet. I release Cozy Pet Care from liability for decisions made honestly and reasonably under this authorization, except for gross negligence or intentional misconduct.</p>

        <SecH>Signature</SecH>
        <p className="text-base text-muted-foreground leading-relaxed mb-3">I certify that I am at least 18 years old and am the pet's owner or authorized representative.</p>
        <Field label="Owner's Printed Name"><input data-testid="em-printed-name" className={inputCls} value={effEmPrinted} onChange={(e) => { setEmPrintedTouched(true); set("emPrinted", e.target.value); }} /></Field>
        <Field label="Owner's Digital Signature"><Sig testid="em-signature" value={f.emSig} onChange={(v) => set("emSig", v)} /></Field>
        <Field label="Date"><DateField testid="em-sign-date" value={f.emDate} onChange={(v) => set("emDate", v)} /></Field>
      </section>

      {/* ============ SEND ============ */}
      <div className="mt-10 pt-8 border-t border-border">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-1">Send to Cozy Pet Care</h2>
        <p className="text-sm text-muted-foreground mb-4">Sends the Pet Care Agreement and the Emergency Care Authorization. Also sends you a copy to your email.</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Your name</label>
            <input data-testid="policy-submitter-name" type="text" value={effName} onChange={(e) => { setSendNameTouched(true); setSendName(e.target.value); }} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Your email <span className="text-primary">(send your copy to:)</span></label>
            <input data-testid="policy-submitter-email" type="email" value={effEmail} onChange={(e) => { setSendEmailTouched(true); setSendEmail(e.target.value); }} placeholder="you@example.com" className={inputCls} />
          </div>
        </div>
        <Button type="submit" data-testid="policy-send-btn" disabled={sending} className="rounded-full h-12 px-8 text-base">
          {sending ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending…</> : <><Send className="w-5 h-5 mr-2" /> Send to Cozy Pet Care</>}
        </Button>
      </div>
    </form>
  );
};
